import { useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';

import { ItemTypes } from './common';
import { SearchBox } from './SearchBox';

const wrapperClass = 'text-xs text-gray-300';

const instructionsClass = 'pl-3 text-12.6px text-slate-400';

const pillClass =
  'm-2.5 rounded-full border border-solid border-slate-500 px-4 py-1.5 text-center text-sm leading-5 text-foreground-default hover:bg-slate-600';

const HeaderItem = ({ label, disabled }: { label: string; disabled?: boolean }): JSX.Element => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: label,
    disabled,
    data: { label, type: ItemTypes.HEADER },
  });

  return (
    <div
      ref={setNodeRef}
      data-testid="file-header-pill"
      className={pillClass}
      style={{
        cursor: disabled ? 'not-allowed' : 'grab',
        opacity: disabled || isDragging ? 0.5 : 1,
      }}
      {...(!disabled ? { ...listeners, ...attributes } : {})}
    >
      {label}
    </div>
  );
};

// Presentational pill rendered inside the @dnd-kit DragOverlay so the column
// being dragged visibly follows the cursor. react-dnd's useDrag gave us a
// native HTML5 drag image for free; @dnd-kit uses pointer events and renders
// nothing unless we draw the moving element ourselves (ENG-2871). Lifted to a
// shared component so the overlay clone matches the real pill exactly.
export const HeaderPillPreview = ({ label }: { label: string }): JSX.Element => (
  <div
    data-testid="file-header-pill-preview"
    className={pillClass}
    style={{
      cursor: 'grabbing',
      boxShadow: '0 6px 16px rgba(0, 0, 0, 0.45)',
      // DragOverlay fixes the clone's wrapper to the source pill's border-box
      // width; the list margin would then eat into the inner width and wrap the
      // label. Drop the margin (meaningless on an overlay) and keep it on one
      // line so the text always fits.
      margin: 0,
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </div>
);

const FileHeadersList = ({
  unmappedHeaders,
  disabled = false,
}: {
  unmappedHeaders: string[];
  disabled?: boolean;
}) => {
  const [searchTerm, setSearchTerm] = useState<string>();

  const handleSearchTermChanged = (text: string) => {
    setSearchTerm(text);
  };

  const matchesSearchText = (value: string) => {
    if (!searchTerm) return true;
    return value.toLowerCase().includes(searchTerm?.toLowerCase());
  };

  const sortedUnmmappedHeaders = [...unmappedHeaders];
  sortedUnmmappedHeaders.sort();

  return (
    <div className={wrapperClass}>
      <p className="mb-1">Search for a column in your loan tape to map to an ORSNN field.</p>
      <SearchBox
        placeholder="Filter columns by name"
        onSearchTermChanged={handleSearchTermChanged}
      />
      <p className={clsx(instructionsClass, 'mt-1')}>
        Select one field and drag and drop onto the respective field on the left
      </p>
      <div className="flex h-[560px] flex-col flex-wrap content-start items-start justify-start overflow-x-auto">
        {sortedUnmmappedHeaders.filter(matchesSearchText).map((header) => {
          return <HeaderItem key={header} label={header} disabled={disabled} />;
        })}
      </div>
    </div>
  );
};

export default FileHeadersList;
